import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getCartId } from 'lib/core/data/quote/cartId/selectors'
import { CartItem } from 'lib/core/data/quote/cart/types'
import { getCartItemUpdate } from 'lib/core/data/quote/cartItemUpdate/selectors'
import { getCartItemRemove } from 'lib/core/data/quote/cartItemRemove/selectors'
import { cartItemUpdate } from 'lib/core/data/quote/cartItemUpdate'
import { cartItemRemove } from 'lib/core/data/quote/cartItemRemove'
import { useCart } from '../useCart'

interface CartItemOptions {
  cartItem: CartItem
}

const MINIMUM_INPUT_VALUE = '0'

export const useCartItem = ({ cartItem }: CartItemOptions) => {
  const dispatch = useDispatch()
  const { getCartIsLoading } = useCart()
  const { id: cartItemId, product, quantity, variantProduct, configurableOptions, customizableOptions, prices } = cartItem
  const [inputQuantity, setInputQuantity] = useState(quantity.toString())
  const cartId = useSelector(getCartId)
  const { isFetching: isUpdateFetching } = useSelector(getCartItemUpdate(cartItemId))
  const { isFetching: isRemoveFetching } = useSelector(getCartItemRemove(cartItemId))
  const quantityHasChanged = inputQuantity !== quantity.toString()
  const productImageUrl = variantProduct?.image.small ?? product.image.small

  function handleSetInputQuantityChange (quantity: string) {
    const number = parseInt(quantity)
    const inputValue = isNaN(number) ? quantity : number
    setInputQuantity(`${inputValue}`)
  }

  function updateCartItemQuantity (quantity?: string) {
    if (!cartId) {
      return
    }

    dispatch(cartItemUpdate(cartId, cartItem, quantity ?? inputQuantity))
    if (quantity) {
      setInputQuantity(quantity)
    }
  }

  function removeCartItem () {
    if (!cartId) {
      return
    }

    dispatch(cartItemRemove(cartId, cartItem))
  }

  return {
    cartId,
    removeCartItem,
    updateCartItemQuantity,
    setInputQuantity: handleSetInputQuantityChange,
    inputQuantity,
    isUpdateFetching,
    isRemoveFetching,
    isCartLoading: getCartIsLoading(),
    isUpdateDisabled: !quantityHasChanged || isUpdateFetching || isRemoveFetching,
    isRemoveDisabled: isRemoveFetching || isUpdateFetching,
    prices,
    imageUrl: productImageUrl,
    productPath: product.getUrlPath(),
    configurableOptions,
    customizableOptions,
    minQuantity: MINIMUM_INPUT_VALUE
  }
}
